/**
 * TraceDetailViewHeader - Extracted header component for TraceDetailView
 *
 * Contains:
 * - Title row with ItemBadge, trace name, options menu
 * - Action buttons (Dataset, Annotate, Queue, Comments)
 * - Metadata badges (timestamp, latency, session, user, environment, release, version, cost, usage)
 *
 * Memoized to prevent unnecessary re-renders when tab state changes.
 */

import { memo, useMemo } from "react";
import {
  type TraceDomain,
  type ScoreDomain,
  AnnotationQueueObjectType,
  LangfuseInternalTraceEnvironment,
} from "@langfuse/shared";
import { type SelectionData } from "@/src/features/comments/contexts/InlineCommentSelectionContext";
import { type WithStringifiedMetadata } from "@/src/utils/clientSideDomainTypes";
import { type ObservationReturnTypeWithMetadata } from "@/src/server/api/routers/traces";
import { ItemBadge } from "@/src/components/ItemBadge";
import { LocalIsoDate } from "@/src/components/LocalIsoDate";
import { DetailHeaderActionsMenu } from "@/src/components/trace/components/_shared/DetailHeaderActionsMenu";
import { NewDatasetItemFromExistingObject } from "@/src/features/datasets/components/NewDatasetItemFromExistingObject";
import { AnnotateDrawer } from "@/src/features/scores/components/AnnotateDrawer";
import { CreateNewAnnotationQueueItem } from "@/src/features/annotation-queues/components/CreateNewAnnotationQueueItem";
import { CommentDrawerButton } from "@/src/features/comments/CommentDrawerButton";
import {
  SessionBadge,
  UserIdBadge,
  EnvironmentBadge,
  ReleaseBadge,
  VersionBadge,
  TargetTraceBadge,
} from "./TraceMetadataBadges";
import { LatencyBadge } from "../ObservationDetailView/ObservationMetadataBadgesSimple";
import {
  CostBadge,
  UsageBadge,
} from "../ObservationDetailView/ObservationMetadataBadgesTooltip";
import { aggregateTraceMetrics } from "@/src/components/trace/lib/trace-aggregation";
import { resolveEvalExecutionMetadata } from "@/src/components/trace/lib/resolve-metadata";
import { useViewPreferences } from "@/src/components/trace/contexts/ViewPreferencesContext";
import { CollapsibleBadgeRow } from "@/src/components/trace/components/_shared/CollapsibleBadgeRow";
import { useIsMobile } from "@/src/hooks/use-mobile";
import { Button } from "@/src/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/utils/tailwind";

export interface TraceDetailViewHeaderProps {
  trace: Omit<WithStringifiedMetadata<TraceDomain>, "input" | "output"> & {
    latency?: number;
    input: string | null;
    output: string | null;
  };
  observations: ObservationReturnTypeWithMetadata[];
  parsedMetadata: unknown;
  projectId: string;
  traceScores: WithStringifiedMetadata<ScoreDomain>[];
  commentCount: number | undefined;
  // Inline comment props
  pendingSelection?: SelectionData | null;
  onSelectionUsed?: () => void;
  isCommentDrawerOpen?: boolean;
  onCommentDrawerOpenChange?: (open: boolean) => void;
}

export const TraceDetailViewHeader = memo(function TraceDetailViewHeader({
  trace,
  observations,
  parsedMetadata,
  projectId,
  traceScores,
  commentCount,
  pendingSelection,
  onSelectionUsed,
  isCommentDrawerOpen,
  onCommentDrawerOpenChange,
}: TraceDetailViewHeaderProps) {
  const { isAnnotationMode } = useViewPreferences();
  const isMobile = useIsMobile();
  const aggregatedMetrics = useMemo(
    () => aggregateTraceMetrics(observations),
    [observations],
  );

  const targetTraceId =
    trace.environment === LangfuseInternalTraceEnvironment.LLMJudge
      ? resolveEvalExecutionMetadata(parsedMetadata)
      : null;

  return (
    <div className="@container shrink-0 space-y-2 border-b p-2">
      {/* Title row with actions */}
      <div className="grid w-full grid-cols-1 items-start gap-2 @2xl:grid-cols-[auto_auto] @2xl:justify-between">
        <div className="flex w-full flex-row items-center gap-1">
          <ItemBadge type="TRACE" isSmall />
          <span
            className={cn(
              "line-clamp-2 min-w-0 font-bold break-all md:break-normal md:wrap-break-word",
              isMobile && "flex-1",
            )}
          >
            {trace.name || trace.id}
          </span>
          <DetailHeaderActionsMenu
            idItems={[{ id: trace.id, name: "Trace ID" }]}
            projectId={projectId}
            webCallout={{
              traceId: trace.id,
              sessionId: trace.sessionId ?? null,
            }}
          />
          {/* Mobile: collapse the action-button cluster into a `⋯` overflow of
              full-width labeled rows, next to the `⋮` utility menu. */}
          {isMobile && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="More actions"
                  className="ml-auto shrink-0"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                // forceMount + hide-when-closed: CommentDrawerButton lives in
                // here, and its deep-link auto-open effect (?comments=open) and
                // controlled inline-selection flow only work while mounted. A
                // default Popover unmounts its content when closed (the default
                // state), silently breaking both. Keep it mounted, just hidden.
                forceMount
                className="flex w-auto min-w-44 flex-col gap-0.5 p-1 data-[state=closed]:hidden"
              >
                <NewDatasetItemFromExistingObject
                  traceId={trace.id}
                  projectId={projectId}
                  input={trace.input}
                  output={trace.output}
                  metadata={trace.metadata}
                  layout="menu"
                />
                {!isAnnotationMode && (
                  <>
                    <AnnotateDrawer
                      projectId={projectId}
                      scoreTarget={{
                        type: "trace",
                        traceId: trace.id,
                      }}
                      scores={traceScores}
                      scoreMetadata={{
                        projectId: projectId,
                        environment: trace.environment,
                      }}
                      layout="menu"
                    />
                    <CreateNewAnnotationQueueItem
                      projectId={projectId}
                      objectId={trace.id}
                      objectType={AnnotationQueueObjectType.TRACE}
                      layout="menu"
                    />
                  </>
                )}
                <CommentDrawerButton
                  projectId={projectId}
                  objectId={trace.id}
                  objectType="TRACE"
                  count={commentCount}
                  layout="menu"
                  pendingSelection={pendingSelection}
                  onSelectionUsed={onSelectionUsed}
                  isOpen={isCommentDrawerOpen}
                  onOpenChange={onCommentDrawerOpenChange}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
        {/* Action buttons (desktop inline cluster) */}
        {!isMobile && (
          <div className="flex h-full flex-wrap content-start items-start justify-start gap-0.5 @2xl:mr-1 @2xl:justify-end">
            <NewDatasetItemFromExistingObject
              traceId={trace.id}
              projectId={projectId}
              input={trace.input}
              output={trace.output}
              metadata={trace.metadata}
              key={trace.id}
              size="sm"
            />
            {/* Hide annotation buttons in annotation mode (panel shown separately) */}
            {!isAnnotationMode && (
              <div className="flex items-start">
                <AnnotateDrawer
                  key={"annotation-drawer-" + trace.id}
                  projectId={projectId}
                  scoreTarget={{
                    type: "trace",
                    traceId: trace.id,
                  }}
                  scores={traceScores}
                  scoreMetadata={{
                    projectId: projectId,
                    environment: trace.environment,
                  }}
                  size="sm"
                />
                <CreateNewAnnotationQueueItem
                  projectId={projectId}
                  objectId={trace.id}
                  objectType={AnnotationQueueObjectType.TRACE}
                  size="sm"
                />
              </div>
            )}
            <CommentDrawerButton
              projectId={projectId}
              objectId={trace.id}
              objectType="TRACE"
              count={commentCount}
              size="sm"
              pendingSelection={pendingSelection}
              onSelectionUsed={onSelectionUsed}
              isOpen={isCommentDrawerOpen}
              onOpenChange={onCommentDrawerOpenChange}
            />
          </div>
        )}
      </div>

      {/* Metadata badges */}
      <div className="flex flex-col gap-2">
        {/* Timestamp */}
        <div className="flex flex-wrap items-center gap-1">
          <LocalIsoDate
            date={trace.timestamp}
            accuracy="millisecond"
            className="text-sm"
          />
        </div>

        {/* Other badges */}
        {!isAnnotationMode && (
          <CollapsibleBadgeRow>
            <LatencyBadge latencySeconds={trace.latency ?? null} />
            <SessionBadge sessionId={trace.sessionId} projectId={projectId} />
            <UserIdBadge userId={trace.userId} projectId={projectId} />
            <TargetTraceBadge
              targetTraceId={targetTraceId}
              projectId={projectId}
            />
            <EnvironmentBadge environment={trace.environment} />
            <ReleaseBadge release={trace.release} />
            <VersionBadge version={trace.version} />
            <CostBadge
              totalCost={aggregatedMetrics.totalCost}
              costDetails={aggregatedMetrics.costDetails}
            />
            {aggregatedMetrics.hasGenerationLike &&
              aggregatedMetrics.usageDetails && (
                <UsageBadge
                  type="GENERATION"
                  inputUsage={aggregatedMetrics.inputUsage}
                  outputUsage={aggregatedMetrics.outputUsage}
                  totalUsage={aggregatedMetrics.totalUsage}
                  usageDetails={aggregatedMetrics.usageDetails}
                />
              )}
          </CollapsibleBadgeRow>
        )}
      </div>
    </div>
  );
});
